import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // 检查本地存储中的认证信息
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // 验证用户是否仍然存在于用户列表中
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        const userExists = users.some(u => u.id === parsedUser.id);
        
        if (userExists) {
          setIsAuthenticated(true);
          setUser(parsedUser);
        } else {
          // 如果用户不存在，清除无效的登录状态
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Invalid user data in localStorage');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
  }, []);

  const register = async (username, password) => {
    if (!username || !password) {
      message.error('用户名和密码不能为空');
      return false;
    }

    try {
      // 检查用户是否已存在
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      if (users.some(u => u.username === username)) {
        message.error('用户名已存在');
        return false;
      }

      // 创建新用户
      const newUser = {
        id: Date.now(),
        username,
        password,
        createdAt: new Date().toISOString()
      };

      // 保存用户信息
      users.push(newUser);
      localStorage.setItem('users', JSON.stringify(users));
      message.success('注册成功，请登录');
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      message.error('注册失败，请重试');
      return false;
    }
  };

  const login = async (username, password) => {
    if (!username || !password) {
      message.error('用户名和密码不能为空');
      return false;
    }

    try {
      // 从本地存储获取用户列表
      const usersStr = localStorage.getItem('users');
      if (!usersStr) {
        message.error('用户不存在，请先注册');
        return false;
      }

      const users = JSON.parse(usersStr);
      if (!Array.isArray(users) || users.length === 0) {
        message.error('用户不存在，请先注册');
        return false;
      }

      const user = users.find(u => u.username === username && u.password === password);

      if (!user) {
        message.error('用户名或密码错误');
        return false;
      }

      // 登录成功，保存用户信息（不包含密码）
      const { password: _, ...safeUser } = user;
      setUser(safeUser);
      setIsAuthenticated(true);
      localStorage.setItem('authToken', `token-${Date.now()}`);
      localStorage.setItem('user', JSON.stringify(safeUser));
      message.success('登录成功');
      return true;
    } catch (error) {
      console.error('Login error:', error);
      message.error('登录失败，请重试');
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    message.success('已退出登录');
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 